import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Input, Select, Spin, DatePicker } from "antd";
import { getCustomers } from "../../services/customerService";
import { createOrder } from "../../services/orderService";

const { TextArea } = Input;

const CreateOrderModal = ({ show, handleClose, onOrderCreated }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [validationErrors, setValidationErrors] = useState({});
  const userId = localStorage.getItem("userId")

  // Fetch customers when modal opens
  useEffect(() => {
    if (show) {
      fetchCustomers();
      // Reset form when modal opens - but don't reset to empty strings
      form.resetFields();
      setValidationErrors({});
    }
  }, [show, form]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await getCustomers(userId, 0, 100); // Adjust page size as needed
      setCustomers(response.data.content || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (changedValues, allValues) => {
    console.log("Form values changed:", changedValues, allValues);
    const newValidationErrors = { ...validationErrors };
    Object.keys(changedValues).forEach(key => {
      if (newValidationErrors[key]) {
        delete newValidationErrors[key];
      }
    });
    setValidationErrors(newValidationErrors);
  };

  const validateForm = (values) => {
    console.log("Validating form with values:", values);
    const errors = {};

    if (!values.customerId) {
      errors.customerId = "Customer is required";
    }

    if (values.orderReference && values.orderReference.length > 50) {
      errors.orderReference = "Order reference cannot exceed 50 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      // Get all form values directly
      const values = await form.validateFields();
      console.log("Form values before validation:", values);
      
      if (!validateForm(values)) {
        return;
      }
      
      setLoading(true);
      try {
        // Add userId from localStorage
        const userId = localStorage.getItem("userId");
        const orderData = {
          ...values,
          // Don't override the orderReference from the form
          createdByUserId: userId ? parseInt(userId) : null,
          // Convert dueDate to YYYY-MM-DD format if provided
          dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null
        };

        // Make sure orderReference isn't overridden
        console.log("Submitting order with data:", orderData);

        // Convert customerId to Long
        orderData.customerId = parseInt(orderData.customerId);
        
        await createOrder(orderData);
        handleClose();
        if (onOrderCreated) {
          onOrderCreated();
        }
      } catch (error) {
        console.error("Error creating order:", error);
        if (error.response?.data?.errors) {
          setValidationErrors(error.response.data.errors);
        }
      } finally {
        setLoading(false);
      }
    } catch (info) {
      console.log('Validate Failed:', info);
    }
  };

  // Filter option function for searching
  const filterOption = (input, option) => {
    // Make sure we have valid string values before attempting toLowerCase()
    const optionLabel = option.label || '';
    return optionLabel.toLowerCase().includes(input.toLowerCase());
  };

  return (
    <Modal
      title="Create New Order"
      open={show}
      onCancel={handleClose}
      maskClosable={false}
      keyboard={false}
      width={800}
      footer={[
        <Button key="back" onClick={handleClose} disabled={loading}>
          İptal
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spin size="small" className="mr-2" /> Oluşturuluyor...
            </>
          ) : (
            "Sipariş oluştur"
          )}
        </Button>
      ]}
    >
      <Spin spinning={loading && !customers.length}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleInputChange}
          initialValues={{
            orderReference: "",
            notes: ""
          }}
        >
          <Form.Item
            required
            name="customerId"
            label={<>Müşteri <span className="text-danger">*</span></>}
            validateStatus={validationErrors.customerId ? "hata" : ""}
            help={validationErrors.customerId}
            rules={[{ required: true, message: "Lütfen bir müşteri seçin" }]}
          >
            <Select
              showSearch
              placeholder="Müşteri arayın ve seçin"
              filterOption={filterOption}
              options={customers.map(customer => ({
                value: customer.id,
                label: `${customer.companyName} - ${customer.contactPerson}`
              }))}
            />
          </Form.Item>

          <Form.Item
            required
            name="orderReference"
            label="Sipariş Referansı"
            validateStatus={validationErrors.orderReference ? "error" : ""}
            help={validationErrors.orderReference}
          >
            <Input 
              placeholder="Sipariş referansı girin" 
              maxLength={50}
              onChange={(e) => {
                // Update form field explicitly
                form.setFieldsValue({ orderReference: e.target.value });
              }}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea
              rows={3}
              placeholder="Sipariş ile ilgili notlarınızı girin"
            />
          </Form.Item>

          <Form.Item
            name="dueDate"
            label="Teslim Tarihi"
          >
            <DatePicker
              format="DD.MM.YYYY"
              placeholder="Teslim tarihi seçin (opsiyonel)"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateOrderModal;